import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert, Card } from "react-bootstrap";
import SafetyInfortuniService from "../../../services/safety/SafetyInfortuniService";
import LavoratoreSearchPicker from "../LavoratoreSearchPicker/LavoratoreSearchPicker";

const EMPTY = {
    tipo_evento: "INFORTUNIO",
    id_lavoratore: "",
    gravita: "",
    data_evento: "",
    ora_evento: "",
    luogo: "",
    descrizione: "",
    dinamica: "",
    giorni_assenza: "0",
    denuncia_inail: "0",
    data_denuncia_inail: "",
    protocollo_inail: "",
    note: "",
};

const TIPI = [
    { value: "INFORTUNIO", label: "Infortunio" },
    { value: "NEAR_MISS", label: "Near miss / mancato infortunio" },
    { value: "MALATTIA_PROFESSIONALE", label: "Malattia professionale" },
];

const GRAVITA = [
    { value: "", label: "— Non indicata —" },
    { value: "LIEVE", label: "Lieve" },
    { value: "MEDIA", label: "Media" },
    { value: "GRAVE", label: "Grave" },
    { value: "MORTALE", label: "Mortale" },
];

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function tipoLabel(v) {
    return TIPI.find(t => t.value === v)?.label || v || "—";
}

function gravitaBadge(g, tipo) {
    if (!g || tipo === "NEAR_MISS") return <span className="text-muted">—</span>;
    const tone = g === "MORTALE" || g === "GRAVE" ? "danger" : g === "MEDIA" ? "warning" : "secondary";
    return <Badge bg={tone} text={tone === "warning" ? "dark" : "light"}>{g}</Badge>;
}

export default function InfortuniPanel({ idAzienda }) {
    const annoCorrente = new Date().getFullYear();
    const [anno, setAnno] = useState(annoCorrente);
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [oreLavoro, setOreLavoro] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savingOre, setSavingOre] = useState(false);

    const parseJson = async (resp) => {
        return resp && typeof resp.json === "function" ? await resp.json() : resp;
    };

    const loadStats = useCallback(async () => {
        if (!idAzienda) { setStats(null); return; }
        try {
            const resp = await SafetyInfortuniService.stats(idAzienda, anno);
            setStats(await parseJson(resp));
        } catch {
            setStats(null);
        }
    }, [idAzienda, anno]);

    const loadOre = useCallback(async () => {
        if (!idAzienda) { setOreLavoro(""); return; }
        try {
            const resp = await SafetyInfortuniService.getOreLavoro(idAzienda, anno);
            const data = await parseJson(resp);
            setOreLavoro(data?.ore_lavorate_annue != null ? String(data.ore_lavorate_annue) : "");
        } catch {
            setOreLavoro("");
        }
    }, [idAzienda, anno]);

    const loadItems = useCallback(async () => {
        if (!idAzienda) { setItems([]); return; }
        setLoading(true);
        setError(null);
        try {
            const resp = await SafetyInfortuniService.byAzienda(idAzienda, anno);
            const data = await parseJson(resp);
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Errore nel caricamento del registro eventi.");
        } finally {
            setLoading(false);
        }
    }, [idAzienda, anno]);

    useEffect(() => { loadStats(); loadOre(); }, [loadStats, loadOre]);
    useEffect(() => { loadItems(); }, [loadItems]);

    const saveOre = async () => {
        if (!idAzienda) return;
        setSavingOre(true);
        setError(null);
        try {
            await SafetyInfortuniService.setOreLavoro(idAzienda, anno, Number(oreLavoro) || 0);
            setMsg("Ore lavorate annue aggiornate.");
            await loadStats();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio ore lavorate.");
        } finally {
            setSavingOre(false);
        }
    };

    const openForm = () => {
        setForm({ ...EMPTY, data_evento: new Date().toISOString().substring(0, 10) });
        setFile(null);
        setError(null);
        setMsg(null);
        setShow(true);
    };

    const submit = async () => {
        if (!idAzienda || !form.data_evento) {
            setError("Indica la data dell'evento.");
            return;
        }
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            fd.append("tipo_evento", form.tipo_evento);
            fd.append("data_evento", form.data_evento);
            if (form.id_lavoratore)       fd.append("id_lavoratore", form.id_lavoratore);
            if (form.gravita)             fd.append("gravita", form.gravita);
            if (form.ora_evento)          fd.append("ora_evento", form.ora_evento);
            if (form.luogo)               fd.append("luogo", form.luogo);
            if (form.descrizione)         fd.append("descrizione", form.descrizione);
            if (form.dinamica)            fd.append("dinamica", form.dinamica);
            fd.append("giorni_assenza", form.giorni_assenza || "0");
            fd.append("denuncia_inail", form.denuncia_inail === "1" ? "1" : "0");
            if (form.data_denuncia_inail) fd.append("data_denuncia_inail", form.data_denuncia_inail);
            if (form.protocollo_inail)    fd.append("protocollo_inail", form.protocollo_inail);
            if (form.note)                fd.append("note", form.note);
            if (file)                     fd.append("file", file);
            await SafetyInfortuniService.create(fd);
            setMsg("Evento registrato.");
            setShow(false);
            setForm(EMPTY);
            setFile(null);
            await loadItems();
            await loadStats();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const onDownload = async (ev) => {
        setError(null);
        try {
            await SafetyInfortuniService.download(ev.id_infortunio, `evento_${ev.id_infortunio}.pdf`);
        } catch (err) {
            setError(err?.message || "Impossibile scaricare il file.");
        }
    };

    const onDelete = async (ev) => {
        if (!ev?.id_infortunio) return;
        if (!window.confirm("Eliminare questo evento dal registro?")) return;
        try {
            await SafetyInfortuniService.deleteInfortunio(ev.id_infortunio);
            await loadItems();
            await loadStats();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                Salva prima i dati dell'azienda per gestire il registro infortuni e near miss.
            </BsAlert>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <h6 className="mb-0">
                    <i className="bi bi-bandaid me-2 text-danger" />
                    Infortuni e near miss
                </h6>
                <div className="d-flex align-items-center gap-2">
                    <Form.Select size="sm" style={{ width: "100px" }} value={anno} onChange={e => setAnno(Number(e.target.value))}>
                        {[annoCorrente, annoCorrente - 1, annoCorrente - 2].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </Form.Select>
                    <Button size="sm" variant="primary" onClick={openForm} disabled={show}>
                        <i className="bi bi-plus-lg me-1" />
                        Registra evento
                    </Button>
                </div>
            </div>

            <div className="text-muted small mb-2">
                Registro eventi con denunce INAIL, giorni di assenza e calcolo indici IF/IG (richiede ore lavorate annue).
            </div>

            {stats && (
                <Row className="g-2 mb-3">
                    <Col xs={6} md={3}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Body className="py-2 px-3">
                                <div className="text-muted small">Infortuni {anno}</div>
                                <div className="fs-5 fw-semibold text-danger">{stats.infortuni ?? 0}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Body className="py-2 px-3">
                                <div className="text-muted small">Near miss</div>
                                <div className="fs-5 fw-semibold text-warning">{stats.near_miss ?? 0}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Body className="py-2 px-3">
                                <div className="text-muted small">Giorni assenza</div>
                                <div className="fs-5 fw-semibold">{stats.giorni_assenza ?? 0}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Body className="py-2 px-3">
                                <div className="text-muted small">IF / IG</div>
                                <div className="small">
                                    {stats.indice_frequenza != null
                                        ? <>IF <strong>{stats.indice_frequenza}</strong> · IG <strong>{stats.indice_gravita}</strong></>
                                        : <span className="text-muted">Imposta ore lavorate</span>}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            <Row className="g-2 mb-3 align-items-end">
                <Col xs={12} md={5}>
                    <Form.Label className="small mb-0">Ore lavorate annue ({anno}) — per indici IF/IG</Form.Label>
                    <Form.Control
                        type="number"
                        size="sm"
                        min="0"
                        value={oreLavoro}
                        onChange={e => setOreLavoro(e.target.value)}
                        placeholder="Es. 52000"
                    />
                </Col>
                <Col xs="auto">
                    <Button size="sm" variant="outline-secondary" onClick={saveOre} disabled={savingOre}>
                        {savingOre ? <Spinner size="sm" animation="border" /> : "Salva ore"}
                    </Button>
                </Col>
                <Col xs={12} md className="text-muted small">
                    IF = (infortuni ÷ ore) × 1.000.000 · IG = (giorni assenza ÷ ore) × 1.000
                </Col>
            </Row>

            {(stats?.denunce_mancanti > 0) && (
                <BsAlert variant="warning" className="py-2 small">
                    <strong>{stats.denunce_mancanti}</strong> infortunio/i senza denuncia INAIL registrata.
                </BsAlert>
            )}

            {error && <BsAlert variant="danger" className="py-2 small">{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2 small" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {show && (
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="fw-semibold mb-2">Nuovo evento</div>
                    <Row className="g-2">
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Tipo evento</Form.Label>
                            <Form.Select size="sm" value={form.tipo_evento} onChange={e => setForm(f => ({ ...f, tipo_evento: e.target.value }))}>
                                {TIPI.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Data evento *</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_evento} onChange={e => setForm(f => ({ ...f, data_evento: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Ora (opz.)</Form.Label>
                            <Form.Control type="time" size="sm" value={form.ora_evento} onChange={e => setForm(f => ({ ...f, ora_evento: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Lavoratore coinvolto (opz.)</Form.Label>
                            <LavoratoreSearchPicker
                                idAzienda={idAzienda}
                                value={form.id_lavoratore}
                                onChange={v => setForm(f => ({ ...f, id_lavoratore: v }))}
                                allowEmpty
                                emptyLabel="— Non specificato —"
                            />
                        </Col>
                        <Col xs={12} md={3}>
                            <Form.Label className="small mb-0">Gravità</Form.Label>
                            <Form.Select size="sm" value={form.gravita} onChange={e => setForm(f => ({ ...f, gravita: e.target.value }))} disabled={form.tipo_evento === "NEAR_MISS"}>
                                {GRAVITA.map(g => <option key={g.value || "x"} value={g.value}>{g.label}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={3}>
                            <Form.Label className="small mb-0">Giorni assenza</Form.Label>
                            <Form.Control type="number" size="sm" min="0" value={form.giorni_assenza} onChange={e => setForm(f => ({ ...f, giorni_assenza: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Luogo</Form.Label>
                            <Form.Control size="sm" value={form.luogo} onChange={e => setForm(f => ({ ...f, luogo: e.target.value }))} placeholder="Sede, cantiere, via…" />
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Descrizione sintetica</Form.Label>
                            <Form.Control size="sm" value={form.descrizione} onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))} />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Dinamica / cause</Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" value={form.dinamica} onChange={e => setForm(f => ({ ...f, dinamica: e.target.value }))} />
                        </Col>
                        {form.tipo_evento === "INFORTUNIO" && (
                            <>
                                <Col xs={12} md={3}>
                                    <Form.Label className="small mb-0">Denuncia INAIL</Form.Label>
                                    <Form.Select size="sm" value={form.denuncia_inail} onChange={e => setForm(f => ({ ...f, denuncia_inail: e.target.value }))}>
                                        <option value="0">Da effettuare / non ancora</option>
                                        <option value="1">Denunciato</option>
                                    </Form.Select>
                                </Col>
                                <Col xs={12} md={3}>
                                    <Form.Label className="small mb-0">Data denuncia</Form.Label>
                                    <Form.Control type="date" size="sm" value={form.data_denuncia_inail} onChange={e => setForm(f => ({ ...f, data_denuncia_inail: e.target.value }))} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Label className="small mb-0">Protocollo INAIL</Form.Label>
                                    <Form.Control size="sm" value={form.protocollo_inail} onChange={e => setForm(f => ({ ...f, protocollo_inail: e.target.value }))} />
                                </Col>
                            </>
                        )}
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Note</Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Allegato (PDF)</Form.Label>
                            <Form.Control type="file" size="sm" accept=".pdf,.PDF" onChange={e => setFile(e.target.files?.[0] || null)} />
                        </Col>
                    </Row>
                    <div className="mt-2 d-flex gap-2">
                        <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" /> : "Salva evento"}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setShow(false)} disabled={saving}>Annulla</Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="text-muted small">Nessun evento registrato per {anno}. Registra infortuni, near miss e malattie professionali.</div>
            ) : (
                <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Data</th>
                            <th>Tipo</th>
                            <th>Lavoratore</th>
                            <th>Gravità</th>
                            <th>Assenza</th>
                            <th>INAIL</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(ev => (
                            <tr key={ev.id_infortunio}>
                                <td>
                                    {fmtDate(ev.data_evento)}
                                    {ev.ora_evento && <div className="text-muted small">{String(ev.ora_evento).substring(0, 5)}</div>}
                                </td>
                                <td>
                                    {tipoLabel(ev.tipo_evento)}
                                    {ev.luogo && <div className="text-muted small">{ev.luogo}</div>}
                                </td>
                                <td className="small">
                                    {ev.lav_cognome ? `${ev.lav_cognome} ${ev.lav_nome || ""}` : "—"}
                                </td>
                                <td>{gravitaBadge(ev.gravita, ev.tipo_evento)}</td>
                                <td>{ev.giorni_assenza > 0 ? `${ev.giorni_assenza} gg` : "—"}</td>
                                <td>
                                    {ev.tipo_evento === "INFORTUNIO" ? (
                                        Number(ev.denuncia_inail) === 1
                                            ? <Badge bg="success">Denunciato</Badge>
                                            : <Badge bg="warning" text="dark">Mancante</Badge>
                                    ) : "—"}
                                </td>
                                <td className="text-end">
                                    {ev.path_file && (
                                        <Button size="sm" variant="danger" className="me-1" onClick={() => onDownload(ev)}>
                                            <i className="bi bi-file-earmark-pdf" />
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline-danger" onClick={() => onDelete(ev)}>
                                        <i className="bi bi-trash" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
